import { useQuery } from '@tanstack/react-query';
import { GetCompnayInfo } from '@/apis/settings';
import { useToken } from '@/common/helper';

export interface ManualShipmentCreationFlags {
  track: boolean;
  quote: boolean;
  book: boolean;
  create: boolean;
  /** "Enable alert owner assignment" in Settings > Company info > Alert settings. */
  ownerAssignmentEnabled: boolean;
  /** false until the setting was read, and if the request failed. */
  ownerAssignmentKnown: boolean;
  loaded: boolean;
}

export const COMPANY_INFO_QUERY_KEY = ['company-info'] as const;

const ALL_ON: ManualShipmentCreationFlags = {
  track: true,
  quote: true,
  book: true,
  create: true,
  // Unlike the manual shipment flags this fails closed: until the setting is
  // known, and if the request fails, owner assignment counts as off, so what it
  // controls does not appear and then disappear.
  ownerAssignmentEnabled: false,
  ownerAssignmentKnown: false,
  loaded: false,
};

const parseBool = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  return fallback;
};

/**
 * Company-level Track / Quote / Book / Create visibility, and whether alert
 * owner assignment is turned on.
 * Shared React Query cache — one company-info fetch per session/tab.
 * Fail-open (all on) if the request fails so a settings outage does not
 * blank the New shipment menu.
 */
export const useManualShipmentCreationFlags =
  (): ManualShipmentCreationFlags => {
    const { token, checkTokenExpiry } = useToken();

    const { data, isFetched, isError } = useQuery({
      queryKey: COMPANY_INFO_QUERY_KEY,
      queryFn: () => {
        const liveToken = checkTokenExpiry();
        if (!liveToken) {
          return Promise.reject(new Error('No token available'));
        }
        return GetCompnayInfo(liveToken);
      },
      enabled: !!token,
      staleTime: 60 * 1000,
      retry: false,
    });

    if (!token || !isFetched) {
      return ALL_ON;
    }

    if (isError) {
      return { ...ALL_ON, loaded: true };
    }

    const payload = data?.data?.data?.data;
    return {
      track: parseBool(payload?.manual_shipment_track_enabled, true),
      quote: parseBool(payload?.manual_shipment_quote_enabled, true),
      book: parseBool(payload?.manual_shipment_book_enabled, true),
      create: parseBool(payload?.manual_shipment_create_enabled, true),
      ownerAssignmentEnabled: parseBool(
        payload?.alert_settings?.owner_assignment_enabled,
        false
      ),
      ownerAssignmentKnown: true,
      loaded: true,
    };
  };
